import React, { useState } from "react";
import api from "../lib/api";

const errMsg = (e) => {
  const d = e.response?.data?.detail;
  return typeof d === "string" ? d : d?.message || "Request failed.";
};

function LaneRows({ rows }) {
  return (
    <div className="border border-zinc-200 rounded-md text-xs" data-testid="internal-test-lanes">
      {rows.map((s) => (
        <div key={`${s.network}:${s.country}`} className="flex justify-between gap-2 px-3 py-1.5 border-b border-zinc-100">
          <span>{s.network} / {s.country}</span>
          <span>expected <b>{s.expected_date}</b> · using <b>{s.using_date || "— not available"}</b>
            {s.fallback && <span className="text-amber-700"> (approved older price)</span>}</span>
        </div>
      ))}
    </div>
  );
}

function Results({ st }) {
  const per = st.results?.[st.customer_id]?.recipients || [];
  return (
    <div className="text-xs space-y-1" data-testid="internal-test-results">
      <div>Status: <b>{st.status}</b></div>
      {per.map((r) => (
        <div key={r.to} className="font-mono">{r.to} · {r.status}{r.mock ? " (test sink)" : ""}{r.error ? ` · ${r.error}` : ""}</div>
      ))}
    </div>
  );
}

export default function InternalBulkTestModal({ onClose }) {
  const [prev, setPrev] = useState(null);
  const [useLatest, setUseLatest] = useState(false);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [st, setSt] = useState(null);

  const load = async (latest = useLatest) => {
    setBusy(true); setErr(""); setAck(false); setSt(null);
    try {
      const { data } = await api.post("/admin/price-report-bulk/internal-test/preview", { use_latest_available: latest });
      setPrev(data);
    } catch (e) { setPrev(null); setErr(errMsg(e)); }
    setBusy(false);
  };
  const poll = async (id) => {
    for (let i = 0; i < 30; i++) {
      const { data } = await api.get(`/admin/price-report-bulk/${id}`);
      setSt(data);
      if (!data.running && data.status !== "sending") return;
      await new Promise((r) => setTimeout(r, 1000));
    }
  };
  const confirm = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/admin/price-report-bulk/internal-test/${prev.campaign_id}/confirm`,
        { snapshot_hash: prev.snapshot_hash });
      setSt(data); await poll(prev.campaign_id);
    } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };

  const canSend = prev && !prev.blocked_reason && prev.outbound_count > 0 && ack && !st;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="internal-test-modal">
      <div className="bg-white rounded-lg w-full max-w-xl p-5 space-y-3 max-h-[90vh] overflow-auto">
        <div className="flex justify-between items-center">
          <h3 className="font-semibold">Internal Bulk Email Test</h3>
          <button onClick={onClose} className="text-zinc-500" data-testid="internal-test-close">Close</button>
        </div>
        <div className="text-xs text-zinc-600">
          Sends the bulk Price Report for the single approved internal test customer to the two approved internal
          addresses only. Normal customer recipients are never selected.
        </div>
        {!prev && !err && (
          <button className="gf-btn gf-btn-outline" disabled={busy} onClick={() => load()} data-testid="internal-test-load">
            {busy ? "Checking…" : "Review internal test"}
          </button>
        )}
        {err && <div className="text-xs text-red-700 border border-red-200 bg-red-50 rounded px-2 py-1.5" data-testid="internal-test-error">{err}</div>}
        {prev && (
          <div className="space-y-2 text-xs" data-testid="internal-test-summary">
            <div>Test customer: <b data-testid="internal-test-customer">{prev.company_name} ({prev.customer_id})</b></div>
            <div>Recipients (one separate email each):
              {prev.internal_recipients.map((r) => <div key={r} className="font-mono" data-testid="internal-test-recipient">{r}</div>)}
            </div>
            {prev.recipients_sent?.length > 0 && <div>Already sent for this date: {prev.recipients_sent.join(", ")}</div>}
            {prev.needs_review?.length > 0 && <div className="text-amber-700">Needs review: {prev.needs_review.map((h) => h.recipient).join(", ")}</div>}
            <LaneRows rows={prev.lane_status || []} />
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={useLatest} data-testid="internal-test-use-latest"
                onChange={(e) => { setUseLatest(e.target.checked); load(e.target.checked); }} />
              Approve latest available older prices where the expected date is missing
            </label>
            {prev.blocked_reason && <div className="text-red-700" data-testid="internal-test-blocked">Blocked: {prev.blocked_reason}</div>}
            <div>Outbound emails: <b data-testid="internal-test-outbound-count">{prev.outbound_count}</b> · Normal customers excluded: <b>yes</b> · Transport: <b>{prev.transport}</b></div>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="internal-test-ack" />
              I confirm this internal test send.
            </label>
            <button className="gf-btn gf-btn-primary" disabled={!canSend || busy} onClick={confirm} data-testid="internal-test-confirm">
              Send internal test ({prev.outbound_count})
            </button>
          </div>
        )}
        {st && <Results st={st} />}
      </div>
    </div>
  );
}
